import type { BoardColumn } from "../calendar-types"
import { calendarDayInstant, localDayKey } from "../overview-move"
import {
  buildRichTooltip,
  escapeTooltipHtml,
  tooltipField,
  tooltipTitle,
} from "../../../../../_shared/_logic/rich-tooltips"

type Interval = [number, number]
export type CalendarDaySummary = {
  appointmentCount: number
  hardMinutes: number
  flexMinutes: number
  workingMinutes: number | null
  blockedMinutes: number
  availableMinutes: number | null
  loadPercent: number | null
  travelMinutes: number
  knownRoutes: number
  unknownRoutes: number
  routeCounts: Record<"GREEN" | "YELLOW" | "RED", number>
  incomplete: boolean
}

const merged = (intervals: Interval[]): Interval[] => {
  const result: Interval[] = []
  for (const [start, end] of intervals.filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0])) {
    const last = result.at(-1)

    if (last && start <= last[1]) last[1] = Math.max(last[1], end)
    else result.push([start, end])
  }

  return result
}
const minutes = (intervals: Interval[]) =>
  intervals.reduce((sum, [start, end]) => sum + (end - start) / 60000, 0)

/** Read-only balance of the same employee-day the calendar displays. Flex windows are not workload. */
export const buildCalendarDaySummary = (
  column: BoardColumn,
  timezone: string,
): CalendarDaySummary | null => {
  const events = [
    ...new Map(column.timedEvents.map((item) => [item.event.id, item.event])).values(),
  ].filter((event) => !["CANCELED", "NOSHOW"].includes(event.status))

  if (!events.some((event) => event.event_type === "APPOINTMENT")) return null

  const day = localDayKey(column.day)
  const dayStart = calendarDayInstant(day, 0, timezone).getTime()
  const dayEnd = calendarDayInstant(day, 1440, timezone).getTime()
  const clip = (start: number, end: number): Interval => [
    Math.max(start, dayStart),
    Math.min(end, dayEnd),
  ]
  let hardMinutes = 0,
    flexMinutes = 0,
    appointmentCount = 0,
    incomplete = false
  const blocks: Interval[] = []
  for (const event of events) {
    const start = Date.parse(event.start_at),
      end = Date.parse(event.end_at)

    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      incomplete = true
      continue
    }

    const interval = clip(start, end)

    if (interval[1] <= interval[0]) continue

    if (event.event_type === "APPOINTMENT") {
      appointmentCount++

      if (event.flex_type === "SOFT") flexMinutes += minutes([interval])
      else hardMinutes += minutes([interval])
    } else blocks.push(interval)
  }
  for (const busy of column.externalBusyIntervals ?? []) {
    if (!Number.isFinite(busy.start.getTime()) || !Number.isFinite(busy.end.getTime()))
      incomplete = true
    else blocks.push(clip(busy.start.getTime(), busy.end.getTime()))
  }

  // Empty days do not acquire another row of status indicators.
  if (!appointmentCount && !incomplete) return null

  const availability = column.availability
  let workingWindows: Interval[] | null = null

  if (availability?.dayOff) workingWindows = []
  else if (availability) {
    const windows = availability.windows ?? [
      { startMinutes: availability.startMinutes!, endMinutes: availability.endMinutes! },
    ]

    if (
      windows.length &&
      windows.every(
        (w) =>
          Number.isFinite(w.startMinutes) &&
          Number.isFinite(w.endMinutes) &&
          w.startMinutes >= 0 &&
          w.endMinutes <= 1440 &&
          w.endMinutes > w.startMinutes,
      )
    ) {
      try {
        workingWindows = merged(
          windows.map((w) => [
            calendarDayInstant(day, w.startMinutes, timezone).getTime(),
            calendarDayInstant(day, w.endMinutes, timezone).getTime(),
          ]),
        )
      } catch {
        /* A window in a DST gap has no reliable capacity. */
      }
    }
  }

  const workingMinutes = workingWindows === null ? null : minutes(workingWindows)
  const blockedMinutes = minutes(
    merged(
      (workingWindows ?? []).flatMap(([start, end]) =>
        blocks.map(([s, e]) => [Math.max(start, s), Math.min(end, e)] as Interval),
      ),
    ),
  )
  const availableMinutes =
    workingMinutes === null ? null : Math.max(0, workingMinutes - blockedMinutes)
  const routeCounts = { GREEN: 0, YELLOW: 0, RED: 0 }
  let travelMinutes = 0,
    knownRoutes = 0,
    unknownRoutes = 0
  const eventIds = new Set(events.map((event) => event.id))
  for (const edge of new Map(column.routeEdges.map((edge) => [edge.id, edge])).values()) {
    if (!eventIds.has(edge.previousEventId) || !eventIds.has(edge.nextEventId)) continue

    // The rounded 10-minute fallback for missing coordinates is not a travel estimate.
    if (
      edge.missingCoordinates ||
      edge.estimatedTravelMinutes == null ||
      !Number.isFinite(edge.estimatedTravelMinutes) ||
      edge.estimatedTravelMinutes < 0
    ) {
      unknownRoutes++
      continue
    }

    knownRoutes++
    travelMinutes += edge.estimatedTravelMinutes

    if (edge.routeGrade === "GREEN" || edge.routeGrade === "YELLOW" || edge.routeGrade === "RED")
      routeCounts[edge.routeGrade]++
  }

  if (appointmentCount > 1 && !knownRoutes && !unknownRoutes) unknownRoutes = 1

  return {
    appointmentCount,
    hardMinutes,
    flexMinutes,
    workingMinutes,
    blockedMinutes,
    availableMinutes,
    loadPercent:
      availableMinutes && !incomplete
        ? Math.round(((hardMinutes + flexMinutes) / availableMinutes) * 100)
        : null,
    travelMinutes,
    knownRoutes,
    unknownRoutes,
    routeCounts,
    incomplete,
  }
}

const duration = (value: number) => {
  const rounded = Math.round(value)

  return rounded >= 60 ? `${Math.floor(rounded / 60)} Std. ${rounded % 60} Min.` : `${rounded} Min.`
}
export const daySummaryPresentation = (summary: CalendarDaySummary) => {
  const note = (text: string) => `<div>${escapeTooltipHtml(text)}</div>`
  const load =
    summary.loadPercent === null
      ? `${summary.appointmentCount} ${summary.appointmentCount === 1 ? "Termin" : "Termine"}`
      : `${summary.loadPercent} %`
  const travel = summary.unknownRoutes
    ? "Wege offen"
    : summary.knownRoutes
      ? `≈ ${Math.round(summary.travelMinutes)} Min. Wege`
      : ""
  const grade = summary.unknownRoutes
    ? "UNKNOWN"
    : summary.routeCounts.RED
      ? "RED"
      : summary.routeCounts.YELLOW
        ? "YELLOW"
        : summary.routeCounts.GREEN
          ? "GREEN"
          : "UNKNOWN"

  return {
    label: [load, travel].filter(Boolean).join(" · "),
    grade,
    tooltip: buildRichTooltip([
      tooltipTitle("Tagesbilanz"),
      tooltipField(
        "Termine",
        `${summary.appointmentCount} · ${duration(summary.hardMinutes + summary.flexMinutes)}`,
      ),
      summary.flexMinutes > 0 && tooltipField("Davon flexibel", duration(summary.flexMinutes)),
      tooltipField(
        "Kalender-Arbeitszeit",
        summary.workingMinutes === null ? "nicht bekannt" : duration(summary.workingMinutes),
      ),
      summary.blockedMinutes > 0 &&
        tooltipField("Abwesenheit / Blocker / extern belegt", duration(summary.blockedMinutes)),
      summary.blockedMinutes > 0 &&
        summary.availableMinutes !== null &&
        tooltipField("Für Termine verfügbar", duration(summary.availableMinutes)),
      note("Terminlast: inkl. Flex und Überschneidungen, ohne Fahrzeit."),
      (summary.knownRoutes > 0 || summary.unknownRoutes > 0) &&
        tooltipField(
          "Wege zwischen Einträgen",
          summary.unknownRoutes
            ? `unvollständig${summary.knownRoutes ? ` · bekannter Anteil ca. ${duration(summary.travelMinutes)}` : ""}`
            : `ca. ${duration(summary.travelMinutes)}`,
        ),
      summary.knownRoutes > 0 &&
        tooltipField(
          "Entfernungen",
          `${summary.routeCounts.GREEN} kurz · ${summary.routeCounts.YELLOW} mittel · ${summary.routeCounts.RED} weit`,
        ),
      summary.knownRoutes > 0 &&
        note(
          "Luftlinien-Schätzung ohne An- und Heimfahrt. Farbpunkt: längste Verbindung (grün bis 3 km, gelb bis 8 km, darüber rot). Grau: Angaben fehlen.",
        ),
      summary.flexMinutes > 0 &&
        note("Flex zählt mit tatsächlicher Dauer. Die Besuchsreihenfolge ist noch offen."),
      summary.incomplete && note("Unvollständige Zeitangaben: Terminlast noch nicht verfügbar."),
    ]),
  }
}
